import React from 'react';
import { getGroupUsers, groupCreate } from '../../../../../helpers/group-helper';

export const useGroupCreateForm = ({ token, navigate }) => {
    const [isLoadingUsers, setIsLoadingUsers] = React.useState(true);
    const [isSaving, setIsSaving] = React.useState(false);
    const [error, setError] = React.useState('');
    const [users, setUsers] = React.useState([]);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [selectedUserIds, setSelectedUserIds] = React.useState([]);
    const [name, setName] = React.useState('');

    React.useEffect(() => {
        const loadUsers = async () => {
            if (!token) {
                setError('No se pudo validar la sesión.');
                setUsers([]);
                setIsLoadingUsers(false);
                return;
            }

            setIsLoadingUsers(true);
            setError('');

            const resp = await getGroupUsers(token);

            if (!resp?.success) {
                setError(resp?.message || 'No se pudo obtener la lista de usuarios del tenant.');
                setUsers([]);
                setIsLoadingUsers(false);
                return;
            }

            setUsers(Array.isArray(resp.data) ? resp.data : []);
            setIsLoadingUsers(false);
        };

        loadUsers();
    }, [token]);

    const usersById = React.useMemo(() => {
        return users.reduce((accumulator, user) => {
            if (!user?._id) return accumulator;
            return {
                ...accumulator,
                [user._id]: user,
            };
        }, {});
    }, [users]);

    const selectedUsers = React.useMemo(() => {
        return selectedUserIds.map((userId) => usersById[userId]).filter(Boolean);
    }, [selectedUserIds, usersById]);

    const filteredUsers = React.useMemo(() => {
        const normalizedSearch = searchTerm.trim().toLowerCase();
        const selectedSet = new Set(selectedUserIds);

        if (!normalizedSearch) {
            return [];
        }

        return users
            .filter((user) => {
                const userId = user?._id;
                if (!userId || selectedSet.has(userId)) {
                    return false;
                }

                const normalizedName = String(user?.name || '').toLowerCase();
                const normalizedEmail = String(user?.email || '').toLowerCase();
                const normalizedRole = String(user?.role || '').toLowerCase();

                return normalizedName.includes(normalizedSearch)
                    || normalizedEmail.includes(normalizedSearch)
                    || normalizedRole.includes(normalizedSearch);
            })
            .slice(0, 20);
    }, [users, selectedUserIds, searchTerm]);

    const handleAddUser = React.useCallback((userId) => {
        setSelectedUserIds((previous) => {
            if (previous.includes(userId)) {
                return previous;
            }

            return [...previous, userId];
        });
        setSearchTerm('');
    }, []);

    const handleRemoveUser = React.useCallback((userId) => {
        setSelectedUserIds((previous) => previous.filter((id) => id !== userId));
    }, []);

    const handleSubmit = React.useCallback(async (event) => {
        event.preventDefault();

        if (!token || isSaving) {
            return;
        }

        setIsSaving(true);
        setError('');

        const payload = {
            name: name.trim(),
            users: selectedUserIds,
        };

        const resp = await groupCreate(token, payload);

        if (!resp?.success) {
            setError(resp?.message || 'No se pudo crear el grupo.');
            setIsSaving(false);
            return;
        }

        navigate('/group/list');
    }, [token, isSaving, name, selectedUserIds, navigate]);

    const handleCancel = React.useCallback(() => {
        navigate('/group/list');
    }, [navigate]);

    return {
        isLoadingUsers,
        isSaving,
        error,
        users,
        searchTerm,
        selectedUsers,
        filteredUsers,
        name,
        setSearchTerm,
        setName,
        handleAddUser,
        handleRemoveUser,
        handleSubmit,
        handleCancel,
    };
};
